/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Converts date and time strings to the stored visit_datetime format.
 *
 * The backend/frontend convention for this field is NOT a true UTC instant:
 * `parseBrtDateTime` (visit-time-conflict.ts) reads a stored value's digits
 * back via `getUTCHours()`/`getUTCDate()` etc., treating them directly as
 * Brazil wall-clock digits with no timezone conversion. This must therefore
 * stamp the user-selected date/time digits verbatim into a Z-suffixed
 * string, not construct a real Date and convert it — the previous
 * implementation (`new Date(...)` parsed in the BROWSER's local timezone,
 * then `setHours(getHours() - 3)`) only produced the right digits when the
 * browser's own timezone happened to be America/Sao_Paulo; in any other
 * timezone (e.g. a UTC CI/E2E runner, or a user whose OS clock isn't set to
 * Brazil) it silently stored the wrong wall-clock time.
 *
 * @param {string} dataStr - Date string (YYYY-MM-DD)
 * @param {string} horaStr - Time string (HH:MM)
 * @returns {string} Stored visit_datetime string, e.g. "2026-08-21T09:00:00.000Z"
 */
export function toIsoFormat(dataStr: string, horaStr: string): string {
  if (!dataStr || !horaStr) return "";
  return `${dataStr}T${horaStr}:00.000Z`;
}

/**
 * Gets old visit data from editing entity.
 * 
 * @param {any} editingEntity - Editing entity
 * @returns {any} Old visit data
 */
export function getOldVisitDatas(editingEntity: any): any {
  if (!editingEntity) return {};
  const dt = editingEntity.visit_datetime;
  return { ...editingEntity, visit_datetime: dt ? new Date(dt).toISOString() : "" };
}

/**
 * Gets default initial values for form.
 * 
 * @param {string} userEmail - User email
 * @returns {any} Default initial values
 */
export function getDefaultInitialValues(userEmail?: string): any {
  return {
    broker_email: userEmail || "",
    status: "pending",
    property_ids: [],
    visit_date: "",
    visit_time: "",
    client_id: "",
    notes: ""
  };
}

/**
 * Parses visit datetime string into date and time.
 * 
 * @param {string} datetime - Datetime string
 * @returns {object} Object with visit_date and visit_time
 */
export function parseVisitDatetime(datetime: string): { visit_date: string; visit_time: string } {
  const parts = datetime.split("T");
  return {
    visit_date: parts[0] || "",
    visit_time: parts[1]?.substring(0, 5) || ""
  };
}

/**
 * Gets entity value or default.
 * 
 * @param {T} value - Value to check
 * @param {T} defaultValue - Default value
 * @returns {T} Value or default
 */
export function getEntityValue<T>(value: T | undefined | null, defaultValue: T): T {
  return value ?? defaultValue;
}

/**
 * Gets initial values from editing entity.
 * 
 * @param {any} editingEntity - Editing entity
 * @param {string} userEmail - User email
 * @returns {any} Initial values
 */
export function getInitialValuesFromEntity(editingEntity: any, userEmail?: string): any {
  if (!editingEntity) {
    return getDefaultInitialValues(userEmail);
  }

  const datetime = getEntityValue(editingEntity.visit_datetime, "");
  const { visit_date, visit_time } = parseVisitDatetime(datetime);

  return {
    property_ids: getEntityValue(editingEntity.property_ids, []),
    visit_date,
    visit_time,
    status: getEntityValue(editingEntity.status, "pending"),
    broker_email: getEntityValue(editingEntity.broker_email, ""),
    client_id: getEntityValue(editingEntity.client_id, ""),
    notes: getEntityValue(editingEntity.notes, ""),
  };
}
